/**
 * Wind forecast points along a route, and the wind hotspots that force extra
 * ones. Shared by the page (UIController) and the route-hazards Lambda (trips
 * started from Android Auto), so both sample wind the same way.
 */

/** Distance between two points in miles. */
function calculateDistance(lat1, lng1, lat2, lng2) {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Waypoints with distance information for the wind forecast: every 25 miles,
 * plus stops (their stop time only shifts later arrival times), the pinned
 * worst wind point and nearby wind hotspots.
 * @param {Array} coordinates - Route coordinates [{ lat, lng }]
 * @returns {Array} Waypoints with distance from previous point and stop info
 */
export function prepareWindWaypoints(coordinates, { routeStops = [], stopMinutes = 30, pinnedWindPoint = null, hotspots = [] } = {}) {
    // Calculate cumulative distance at each coordinate
    let totalDistance = 0;
    const distances = [0];
    
    for (let i = 1; i < coordinates.length; i++) {
        const prev = coordinates[i - 1];
        const curr = coordinates[i];
        const segmentDistance = calculateDistance(
            prev.lat, prev.lng,
            curr.lat, curr.lng
        );
        totalDistance += segmentDistance;
        distances.push(totalDistance);
    }
    
    console.log(`📍 Found ${routeStops.length} stops for this route`);
    
    // FIXED 25-mile sampling — deterministic regardless of stops
    // Stops only affect arrival time, never which points get sampled
    const SAMPLE_INTERVAL_MILES = 25;
    const sampledWaypoints = [];
    
    // Always include the first point
    sampledWaypoints.push({
        lat: coordinates[0].lat,
        lng: coordinates[0].lng,
        distanceFromPrevious: 0,
        cumulativeDistance: 0
    });
    
    // Sample at fixed 25-mile intervals
    let nextSampleMile = SAMPLE_INTERVAL_MILES;
    for (let i = 1; i < coordinates.length; i++) {
        if (distances[i] >= nextSampleMile) {
            sampledWaypoints.push({
                lat: coordinates[i].lat,
                lng: coordinates[i].lng,
                distanceFromPrevious: distances[i] - (sampledWaypoints.length > 0 
                    ? sampledWaypoints[sampledWaypoints.length - 1].cumulativeDistance : 0),
                cumulativeDistance: distances[i]
            });
            nextSampleMile = distances[i] + SAMPLE_INTERVAL_MILES;
        }
    }
    
    // Always include the last point
    const lastCoord = coordinates[coordinates.length - 1];
    const lastSampled = sampledWaypoints[sampledWaypoints.length - 1];
    if (lastSampled.cumulativeDistance < totalDistance - 5) {
        sampledWaypoints.push({
            lat: lastCoord.lat,
            lng: lastCoord.lng,
            distanceFromPrevious: totalDistance - lastSampled.cumulativeDistance,
            cumulativeDistance: totalDistance
        });
    }
    
    // Now layer stops on top — find the nearest sampled waypoint to each stop
    // and mark it with stop duration (for arrival time calculation only)
    for (const stop of routeStops) {
        let nearestIdx = -1;
        let nearestDist = Infinity;
        
        for (let i = 0; i < sampledWaypoints.length; i++) {
            const wp = sampledWaypoints[i];
            const dist = Math.sqrt(
                Math.pow(wp.lat - stop.lat, 2) + 
                Math.pow(wp.lng - stop.lng, 2)
            );
            if (dist < nearestDist) {
                nearestDist = dist;
                nearestIdx = i;
            }
        }
        
        // If the stop is close to an existing sample point, add stop duration there
        // If it's far from any sample point, insert it as an additional waypoint
        if (nearestDist < 0.15) { // ~10 miles in degrees
            sampledWaypoints[nearestIdx].stopType = 'custom';
            sampledWaypoints[nearestIdx].stopDurationMinutes = stopMinutes;
            sampledWaypoints[nearestIdx].stopName = stop.name;
        } else {
            // Find where along the route this stop falls and insert it
            let insertIdx = sampledWaypoints.length;
            for (let i = 0; i < sampledWaypoints.length; i++) {
                if (sampledWaypoints[i].cumulativeDistance > (stop.distanceAlongRoute || 0)) {
                    insertIdx = i;
                    break;
                }
            }
            const prevWp = sampledWaypoints[insertIdx - 1] || sampledWaypoints[0];
            sampledWaypoints.splice(insertIdx, 0, {
                lat: stop.lat,
                lng: stop.lng,
                distanceFromPrevious: (stop.distanceAlongRoute || prevWp.cumulativeDistance) - prevWp.cumulativeDistance,
                cumulativeDistance: stop.distanceAlongRoute || prevWp.cumulativeDistance,
                stopType: 'custom',
                stopDurationMinutes: stopMinutes,
                stopName: stop.name
            });
        }
        console.log(`   ⛽ Stop: ${stop.name} (+${stopMinutes} min)`);
    }
    
    // Recalculate distanceFromPrevious after any insertions
    for (let i = 1; i < sampledWaypoints.length; i++) {
        sampledWaypoints[i].distanceFromPrevious = 
            sampledWaypoints[i].cumulativeDistance - sampledWaypoints[i - 1].cumulativeDistance;
    }
    
    // Phase 19: Always include the pinned worst wind point if it exists
    // This ensures the worst wind location is never lost on recalculate
    if (pinnedWindPoint && pinnedWindPoint.lat && pinnedWindPoint.lng) {
        const alreadyIncluded = sampledWaypoints.some(wp => {
            const dist = Math.sqrt(
                Math.pow(wp.lat - pinnedWindPoint.lat, 2) +
                Math.pow(wp.lng - pinnedWindPoint.lng, 2)
            );
            return dist < 0.01; // ~0.7 miles — close enough
        });
        
        if (!alreadyIncluded) {
            // Find where along the route this point falls
            let pinnedDistance = 0;
            let closestDist = Infinity;
            for (let i = 0; i < coordinates.length; i++) {
                const d = Math.sqrt(
                    Math.pow(coordinates[i].lat - pinnedWindPoint.lat, 2) +
                    Math.pow(coordinates[i].lng - pinnedWindPoint.lng, 2)
                );
                if (d < closestDist) {
                    closestDist = d;
                    pinnedDistance = distances[i];
                }
            }
            
            // Insert at the correct position
            let insertIdx = sampledWaypoints.length;
            for (let i = 0; i < sampledWaypoints.length; i++) {
                if (sampledWaypoints[i].cumulativeDistance > pinnedDistance) {
                    insertIdx = i;
                    break;
                }
            }
            
            sampledWaypoints.splice(insertIdx, 0, {
                lat: pinnedWindPoint.lat,
                lng: pinnedWindPoint.lng,
                distanceFromPrevious: 0,
                cumulativeDistance: pinnedDistance,
                isPinnedWindPoint: true
            });
            
            // Recalculate distanceFromPrevious again after pinned point insertion
            for (let i = 1; i < sampledWaypoints.length; i++) {
                sampledWaypoints[i].distanceFromPrevious = 
                    sampledWaypoints[i].cumulativeDistance - sampledWaypoints[i - 1].cumulativeDistance;
            }
            
            console.log(`   📌 Pinned worst wind point at ${pinnedWindPoint.lat.toFixed(3)}, ${pinnedWindPoint.lng.toFixed(3)} (was ${pinnedWindPoint.originalSpeed} mph)`);
        }
    }
    
    // Inject wind hotspot forced sample points (if any returned from API)
    // Same pattern as pinned wind point — deduplicate within 5 miles of existing points
    if (hotspots && hotspots.length > 0) {
        let hotspotsAdded = 0;
        for (const hotspot of hotspots) {
            // Check if hotspot is within 5 miles of any existing sample point
            const tooClose = sampledWaypoints.some(wp => {
                const dist = calculateDistance(wp.lat, wp.lng, hotspot.lat, hotspot.lng);
                return dist < 5;
            });

            if (tooClose) continue; // Deduplicate — skip this hotspot

            // Find where along the route this hotspot falls
            let hotspotDistance = 0;
            let closestDist = Infinity;
            for (let i = 0; i < coordinates.length; i++) {
                const d = Math.sqrt(
                    Math.pow(coordinates[i].lat - hotspot.lat, 2) +
                    Math.pow(coordinates[i].lng - hotspot.lng, 2)
                );
                if (d < closestDist) {
                    closestDist = d;
                    hotspotDistance = distances[i];
                }
            }

            // Only inject if hotspot is actually near the route (within ~30 miles in degrees)
            if (closestDist > 0.45) continue;

            // Insert at the correct position
            let insertIdx = sampledWaypoints.length;
            for (let i = 0; i < sampledWaypoints.length; i++) {
                if (sampledWaypoints[i].cumulativeDistance > hotspotDistance) {
                    insertIdx = i;
                    break;
                }
            }

            sampledWaypoints.splice(insertIdx, 0, {
                lat: hotspot.lat,
                lng: hotspot.lng,
                distanceFromPrevious: 0,
                cumulativeDistance: hotspotDistance,
                isWindHotspot: true,
                hotspotName: hotspot.name
            });
            hotspotsAdded++;
        }

        if (hotspotsAdded > 0) {
            // Recalculate distanceFromPrevious after hotspot insertions
            for (let i = 1; i < sampledWaypoints.length; i++) {
                sampledWaypoints[i].distanceFromPrevious =
                    sampledWaypoints[i].cumulativeDistance - sampledWaypoints[i - 1].cumulativeDistance;
            }
            console.log(`   🔴 Injected ${hotspotsAdded} wind hotspot sample points`);
        }
    }

    console.log(`🌬️ Prepared ${sampledWaypoints.length} waypoints for wind forecast (${Math.round(totalDistance)} miles total, ${routeStops.length} stops, sampling every ${SAMPLE_INTERVAL_MILES} mi)`);
    return sampledWaypoints;
}

/**
 * Fetch wind hotspots along a route corridor from the backend.
 * Returns hotspots within the route's bounding box.
 * Graceful fallback — returns empty array on any error so normal sampling continues.
 */
export async function fetchWindHotspots(coordinates, backendUrl) {
    try {
        // Hotspots are US data only (WYDOT/CDOT corridors); the page
        // checks the market before calling.
        if (!coordinates || coordinates.length < 2) return [];

        // Build bounding box from route coordinates with padding
        const lats = coordinates.map(c => c.lat);
        const lngs = coordinates.map(c => c.lng);
        const bounds = {
            north: Math.max(...lats) + 0.3,
            south: Math.min(...lats) - 0.3,
            east: Math.max(...lngs) + 0.3,
            west: Math.min(...lngs) - 0.3
        };

        if (!backendUrl) return [];

        const response = await fetch(`${backendUrl}/api/wind-hotspots`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ bounds })
        });

        if (!response.ok) return [];

        const data = await response.json();
        const hotspots = data.hotspots || [];

        if (hotspots.length > 0) {
            console.log(`🔴 Found ${hotspots.length} wind hotspots in route corridor`);
        }

        return hotspots;
    } catch (error) {
        // Graceful fallback — hotspot fetch failure never blocks route calculation
        console.warn('Wind hotspots fetch failed (continuing without):', error.message);
        return [];
    }
}
